/**
 * @file jobSearch.ts
 *
 * @fileoverview Provides job search query.
 */
import { gql } from '@apollo/client';
import CUSTOM_FIELDS from '~/graphql/queries/job/customJobFields.gql';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getJobFragment } from '~/jobiqo/components/Job/graphql/fragments/jobFragment.js';
import ORGANIZATION_ON_JOB_FRAGMENT from '~/jobiqo/components/Job/graphql/fragments/organizationOnJobFragment.gql';
import CUSTOM_ORGANIZATION_FIELDS from '~/graphql/queries/organization/customOrganizationFields.gql';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

const getRuntimeFrontendConfig = (frontendConfig = null) => {
  if (frontendConfig) {
    return frontendConfig;
  }

  return typeof window === 'undefined'
    ? getServerFrontendConfig()
    : getCurrentFrontendConfig();
};

export const getJobById = (
  isAgencyFeatureEnabled: boolean | null = null,
  enabledModules: string[] | null = null,
) => {
  const jobsConfig = getRuntimeFrontendConfig().JOBS || {};
  const cipherJobsEnabled = jobsConfig.JOB_CIPHER_JOBS_ENABLED;
  const remoteJobsEnabled = jobsConfig.REMOTE_JOBS_FEATURE_ACTIVE;
  const trackingPixelEnabled = jobsConfig.JOB_TRACKING_PIXEL_ENABLED;
  const jobPtAttachmentEnabled = jobsConfig.JOB_PT_ATTACHMENT_ENABLED;
  const showOrganizationPresentationOnDetailPage =
    jobsConfig.SHOW_ORGANIZATION_PRESENTATION_ON_DETAIL_PAGE;
  const jobSolrBackfill = jobsConfig.JOB_SOLR_BACKFILL;

  return gql`
  ${getJobFragment(undefined, enabledModules)}
  ${ORGANIZATION_ON_JOB_FRAGMENT}
  ${CUSTOM_FIELDS}
  ${CUSTOM_ORGANIZATION_FIELDS}
  query jobById(
    $id: Int!
    ${jobSolrBackfill ? '$backfillid: String' : ''}
    $getOrgProfileDetails: Boolean = false
  ) {
    jobById(id: $id${jobSolrBackfill ? ', backfillid: $backfillid' : ''}) {
      errors
      job: content {
        ... on Job {
          ...JobFragment
          organizationProfile @include(if: $getOrgProfileDetails) {
            ...OrganizationOnJobFragment
            ...CustomOrganizationFields
            ${
              showOrganizationPresentationOnDetailPage
                ? `
               presentation
               contactPersons {
                 id
               }
               size {
                 tid
               }
               occupation {
                 tid
               }
               fieldsOfStudy {
                 tid
               }
               `
                : ''
            }
          }
          ${cipherJobsEnabled ? 'isCipher' : ''}
          ${trackingPixelEnabled ? 'trackingPixel' : ''}
          ${trackingPixelEnabled ? 'clickApplyTrackingPixel' : ''}
          ${
            remoteJobsEnabled
              ? `
            remoteOptions {
              tid
              label
            }
          `
              : ' '
          }
          ${jobPtAttachmentEnabled ? 'attachmentUrl' : ''}
          ${isAgencyFeatureEnabled ? 'organizationAssignment' : ''}
          ...CustomFields
        }
      }
    }
  }
`;
};

export const getJobByPath = () => {
  const jobsConfig = getRuntimeFrontendConfig().JOBS || {};
  const cipherJobsEnabled = jobsConfig.JOB_CIPHER_JOBS_ENABLED;
  const remoteJobsEnabled = jobsConfig.REMOTE_JOBS_FEATURE_ACTIVE;
  const trackingPixelEnabled = jobsConfig.JOB_TRACKING_PIXEL_ENABLED;
  const jobPtAttachmentEnabled = jobsConfig.JOB_PT_ATTACHMENT_ENABLED;
  const showOrganizationPresentationOnDetailPage =
    jobsConfig.SHOW_ORGANIZATION_PRESENTATION_ON_DETAIL_PAGE;

  return gql`
  ${getJobFragment(undefined)}
  ${CUSTOM_FIELDS}
  ${ORGANIZATION_ON_JOB_FRAGMENT}
  ${CUSTOM_ORGANIZATION_FIELDS}
  query JOB_BY_PATH($path: String!, $language: String) {
    jobByPath: route(path: $path, language: $language) {
      ... on ErrorResponse {
        code
        reason
        __typename
      }
      ... on RedirectResponse {
        code
        target
        __typename
      }
      ... on ContentResponse {
        content {
          ... on Job {
            ...JobFragment
            organizationProfile {
            ...OrganizationOnJobFragment
            ...CustomOrganizationFields
              ${
                showOrganizationPresentationOnDetailPage
                  ? `
               presentation
               contactPersons {
                 id
               }
               size {
                 tid
               }
               occupation {
                 tid
               }
               fieldsOfStudy {
                 tid
               }
               `
                  : ''
              }
            }
            ${cipherJobsEnabled ? 'isCipher' : ''}
            ${trackingPixelEnabled ? 'trackingPixel' : ''}
            ${trackingPixelEnabled ? 'clickApplyTrackingPixel' : ''}
            ${
              remoteJobsEnabled
                ? `
            remoteOptions {
              tid
              label
            }
          `
                : ' '
            }
            ${jobPtAttachmentEnabled ? 'attachmentUrl' : ''}
            ...CustomFields
          }
        }
      }
    }
  }
`;
};
